import _ from "lodash";
import { Card, Col } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import ExpandedImageView from "./ExpandedImageView";
import { IMAGE_OPTIONS_TYPE, RESPONSE_EQUIPMENT_RENAME_FAILED_CODE } from './../../../Common/constants';
import { checkForAPISuccess, responseToastHandler, showErrorToast, showInfoToast, showSuccessToast } from "../../../Common/commonFunctions";
import { fetchEquipmentByEquipmentId, renameEntity, updateEquipmentInfoByEquipmentId } from "../../../api";
import LabelValuePair from "../Common/LabelValuePair";
import { DESELECT_EQUIPMENT, SET_EQUIPMENT_DETAIL_LOADING, SET_EQUIPMENTS,
        SET_ONGOING_EDIT_GLOBALLY, UPDATE_EQUIPMENT_INFORMATION } from './../../../store/actions';
import EquipmentUnitList from "./EquipmentUnitList";
import ImageCarousel from "./ImageCarousel";
import { Icon } from "@iconify/react";
import ToolTip from "../Common/Tooltip";

const EquipmentDetail = props => {

    const dispatch = useDispatch();
    const isEquipmentLoading = useSelector(state => state.equipment.equipmentLoading);
    const isEditOngoing = useSelector(state => state.equipment.isEditOngoing);
    const isLockedProfile = useSelector(state => state.equipment.isLockedProfile);
    const currentEnlargedImageType = useSelector(state => state.equipment.currentEnlargedImageType);
    const isComponentLoading = useSelector(state => state.equipment.componentLoading);

    const useQueryConfiguration = {
        enabled: false, retry: false, retryOnMount: false,
        refetchOnMount: false, refetchOnWindowFocus: false
    }
    const [utils, setUtils] = useState({
        initialValue: { name: "", location: "" }, isLocationEditActive: false,
        currentValue: { name: "", location: "" }, isNameEditActive: false
    });

    const  fetchEquipment = async () => {
        const response = await fetchEquipmentByEquipmentId(props.equipmentId);
        return response?.data;
    }

    const fetchEquipmentQuery = useQuery("fetch-equipment-details", fetchEquipment, {...useQueryConfiguration});

    const updateEquipmentLocation = async () => {
        const response = await updateEquipmentInfoByEquipmentId(
            props.equipmentId,
            { location: utils.currentValue.location }
        );
        return response?.data;
    }

    const updateEquipmentLocationQuery = useQuery('update-equipment', updateEquipmentLocation, { ...useQueryConfiguration });

    const updateEquipmentName = async () => {
        const response = await renameEntity(
            "equipment",
            { id: props.equipmentId, name: utils.currentValue.name }
        );
        return response?.data;
    }

    const updateEquipmentNameMutation = useMutation(updateEquipmentName);

    useEffect(() => {
        setUtils({});
        if(!_.isEmpty(props.equipmentId)) {
            dispatch({ type: SET_EQUIPMENT_DETAIL_LOADING, payload: true });
            fetchEquipmentQuery.refetch();
        }
        else {
            dispatch({ type: DESELECT_EQUIPMENT });
        }
    }, [props.equipmentId]);

    useEffect(() => {
        if(updateEquipmentLocationQuery.isFetched) {
            if(updateEquipmentLocationQuery.isSuccess && checkForAPISuccess(updateEquipmentLocationQuery?.data?.statusCode)) {
                setUtils({ ...utils, isLocationEditActive: false, initialValue: _.cloneDeep(utils.currentValue) });
                dispatch({
                    type: UPDATE_EQUIPMENT_INFORMATION,
                    payload: { id: props.equipmentId, location: utils.currentValue.location, name: utils.currentValue.name }
                });
                showSuccessToast("Updated loction successfully");
            }
            else {
                showErrorToast("Failed to update the information");
            }
            updateEquipmentLocationQuery.remove();
        }
        else if(fetchEquipmentQuery.isFetched) {
            if(fetchEquipmentQuery.isSuccess && checkForAPISuccess(fetchEquipmentQuery?.data?.statusCode)) {
                const data = fetchEquipmentQuery?.data?.data;
                let details = { name: data?.name, location: data?.location };
                setUtils({
                    initialValue: _.cloneDeep(details),
                    currentValue: _.cloneDeep(details), isLocationEditActive: false
                });
                dispatch({
                    type: SET_EQUIPMENTS,
                    payload: { equipmentUnits: data?.equipmentUnits, equipmentPictures: data?.pictures }
                });
            }
            else {
                showErrorToast("Failed to fetch equipment information");
                dispatch({ type: SET_EQUIPMENT_DETAIL_LOADING, payload: false });
            }
            fetchEquipmentQuery.remove();
        }
        else if(updateEquipmentNameMutation.isSuccess) {
            if(checkForAPISuccess(updateEquipmentNameMutation?.data?.statusCode)) {
                showSuccessToast("Equipment name updated successfully");
                setUtils({ ...utils, initialValue: _.cloneDeep(utils.currentValue), isNameEditActive: false });
                dispatch({
                    type: UPDATE_EQUIPMENT_INFORMATION,
                    payload: { id: props.equipmentId, location: utils.currentValue.location, name: utils.currentValue.name }
                });
            }
            else if(updateEquipmentNameMutation?.data?.statusCode === RESPONSE_EQUIPMENT_RENAME_FAILED_CODE) {
                showInfoToast("Provided name is used by another equipment in this profile. Please provide different name", 7000);
            }
            else {
                showErrorToast("Failed to update equipment name");
            }
            updateEquipmentNameMutation.reset();
        }
    }, [fetchEquipmentQuery.isFetched, updateEquipmentLocationQuery.isFetched, updateEquipmentNameMutation.isSuccess]);

    const handleHeaderIcons = (action, type) => {
        if (_.isEqual(action, "EDIT")) {
            if(_.isEqual("LOCATION", type)) {
                setUtils({ ...utils, isLocationEditActive: true });
            }
            else if(_.isEqual("NAME", type)) {
                setUtils({ ...utils, isNameEditActive: true });
            }
            dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: true });
        }
        else if (_.isEqual(action, "CANCEL")) {
            setUtils({
                ...utils,
                isNameEditActive: false,
                isLocationEditActive: false,
                currentValue: _.cloneDeep(utils.initialValue)
            });
            dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: false });
        }
        else {
            if(_.isEqual(type, "LOCATION")) {
                updateEquipmentLocationQuery.refetch();
            }
            else {
                updateEquipmentNameMutation.mutate();
            }
        }
    }

    const returnIcon = (toolTipKey, toolTipValue, icon, className, callBack, color) =>
        <ToolTip
            toolTipKey={toolTipKey}
            toolTipValue={toolTipValue}
            overlayComponent={
                <Icon color={color} className={className} icon={icon} width={16} height={16} onClick={() => callBack()} />
            }
        />

    const returnEquipmentFields = (label, value, viewMode, changeCallBack) => 
        <LabelValuePair
            label={label}
            viewMode={viewMode}
            handleChange={(value) => _.isFunction(changeCallBack) && changeCallBack(value)}
            value={value}
        />

    return (
        <>
            {
                !_.isEqual(currentEnlargedImageType, IMAGE_OPTIONS_TYPE[1]) ?
                <>
                    {
                        !_.isEmpty(currentEnlargedImageType) && _.isEqual(currentEnlargedImageType, IMAGE_OPTIONS_TYPE[0]) &&
                            <ExpandedImageView target={IMAGE_OPTIONS_TYPE[0]} title={utils.currentValue?.name} />
                    }
                    <Col
                        className={`p-1`}
                        {...props.breakPoints}
                    >
                        <Card className="p-0 h-100">
                            <Card.Header className="h-50-px">
                                {
                                    !_.isEmpty(utils.currentValue?.name) &&
                                        <>
                                            {
                                                utils.isNameEditActive ?
                                                    <>
                                                        {
                                                            updateEquipmentNameMutation.isLoading && utils.currentValue.name !== utils.initialValue.name ?
                                                                <ClipLoader color="#075978" size={25} />
                                                                    :
                                                                <div className="vertical-center-align">
                                                                    <input
                                                                        className="rename-text-box"
                                                                        value={utils.currentValue.name}
                                                                        onChange={(e) => setUtils({ ...utils, currentValue: { ...utils.currentValue, name: e.target.value } })}
                                                                    />
                                                                    {
                                                                        returnIcon(
                                                                            "cancel-equipment-component-header",
                                                                            "Cancel",
                                                                            "feather-x",
                                                                            "cursor-pointer mx-2",
                                                                            () => handleHeaderIcons("CANCEL"),
                                                                            "grey"
                                                                        )
                                                                    }
                                                                    {
                                                                        !_.isEmpty(utils.currentValue.name) &&
                                                                        !_.isEqual(utils.currentValue.name, utils.initialValue.name) &&
                                                                            returnIcon(
                                                                                "update-equipment-component-header",
                                                                                "Update",
                                                                                "feather-check",
                                                                                "cursor-pointer mx-2",
                                                                                () => handleHeaderIcons("UPDATE", "NAME")
                                                                            )
                                                                    }
                                                                </div>
                                                        }
                                                    </>
                                                        :
                                                    <div className="equipment-component-header-container">
                                                        <h5 className="equipment-component-header ml-3">
                                                            {
                                                                utils.currentValue?.name
                                                            }
                                                        </h5>
                                                        {
                                                            !isLockedProfile &&
                                                                <Icon
                                                                    width={18}
                                                                    height={18}
                                                                    icon={"feather-edit-2"}
                                                                    color={isComponentLoading || isEditOngoing ? "gray" : "#075978"}
                                                                    className={isComponentLoading || isEditOngoing ? "disable-click" : "cursor-pointer"}
                                                                    onClick={() => handleHeaderIcons("EDIT", "NAME")}
                                                                />
                                                        }
                                                    </div>
                                            }
                                        </>
                                }
                            </Card.Header>
                            <Card.Body className="p-0">
                                {
                                    _.isEmpty(utils.currentValue?.name) ?
                                        <div className="equipment-list-flex-box equipment-scroll-viewport-height px-3">
                                            <center>
                                                {
                                                    isEquipmentLoading ?
                                                        <ClipLoader color="#075978" />
                                                            :
                                                        <b>
                                                            PLEASE SELECT AN EQUIPMENT TO VIEW THE LIST OF EQUIPMENT UNITS IN IT
                                                        </b>
                                                }
                                            </center>
                                        </div>
                                        :
                                        <>
                                            <div className="equipment-information-header">
                                                <b className="ml-1">Equipment information</b>
                                                <div>
                                                    {
                                                        utils.isLocationEditActive ?
                                                            <>
                                                                {
                                                                    (
                                                                        !_.isEmpty(utils.currentValue?.name) &&
                                                                        utils.isLocationEditActive && 
                                                                        !_.isEqual(utils.currentValue.location, utils.initialValue.location)
                                                                    ) &&
                                                                        returnIcon(
                                                                            "equipment-location-update",
                                                                            "Update location",
                                                                            "feather-check",
                                                                            "cursor-pointer mr-3",
                                                                            () => handleHeaderIcons("UPDATE", "LOCATION")
                                                                        )
                                                                }
                                                                {
                                                                    returnIcon(
                                                                        "equipment-localtion-cancel",
                                                                        "Cancel",
                                                                        "feather-x",
                                                                        "cursor-pointer mr-3",
                                                                        () => handleHeaderIcons("CANCEL")
                                                                    )
                                                                }
                                                            </>
                                                                :
                                                            !isLockedProfile &&
                                                                returnIcon(
                                                                    "equipment-location-edit",
                                                                    "Edit location",
                                                                    "feather-edit",
                                                                    `mr-3 ${isComponentLoading || isEditOngoing ? "disable-click" : "cursor-pointer"}`,
                                                                    () => handleHeaderIcons("EDIT", "LOCATION"),
                                                                    isComponentLoading || isEditOngoing ? "gray" : "#075978"
                                                                )
                                                    }
                                                </div>
                                            </div>
                                            {
                                                updateEquipmentLocationQuery.isFetching && utils.currentValue.location !== utils.initialValue.location ?
                                                    <div className="text-center">
                                                        <ClipLoader size={30} className="my-1" color="#075978" />
                                                    </div>
                                                        :
                                                    returnEquipmentFields(
                                                        "Location",
                                                        utils.currentValue.location,
                                                        !utils.isLocationEditActive,
                                                        (value) => setUtils({ ...utils, currentValue: { ...utils.currentValue, location: value } })
                                                    )
                                            }
                                            <ImageCarousel target={IMAGE_OPTIONS_TYPE[0]} title={"Equipment Images"} />
                                            <EquipmentUnitList equipmentName={utils.currentValue?.name} equipmentId={props.equipmentId} />
                                        </>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </>
                :
                <></>
            }
        </>
    )
}

export default EquipmentDetail;